import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  loginWithEmail,
  clearError,
  requestAdminHostOTP,
  verifyAdminHostOTP,
  requestOTP,
  verifyOTP,
  verifyUsherOTP,
} from "../../store/slices/authSlice";
import { addNotification } from "../../store/slices/uiSlice";
import { secureStorage } from "../../utils/secureStorage";
import { DEFAULT_COUNTRY } from "../../data/countryCodes";
import { apiClient } from "../../services/apiClient";
import {
  OTPVerificationForm,
  EmailPasswordForm,
  AuthErrorDisplay,
} from "../../components/Auth";
import { PhoneInputWithValidation, PhoneValidationResponse } from "../../components/UI/PhoneInputWithValidation";
import { parseE164Phone } from "../../utils/phoneValidationV2";
import { findCountryByCode } from "../../data/countryCodes";
import { Mail, CheckCircle } from "lucide-react";
import { logError } from "../../utils/errorLogger";
import Button from "../../components/UI/Button";

type LoginMethod = "phone" | "email";

const UnifiedLoginPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated, user } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("phone");
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>(
    DEFAULT_COUNTRY.code
  );
  const [phoneValidation, setPhoneValidation] = useState<PhoneValidationResponse | null>(null);
  const [rememberMe, setRememberMe] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState("");
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [verifyingToken, setVerifyingToken] = useState(false);
  const [localError, setLocalError] = useState("");
  const [otpEndpoint, setOtpEndpoint] = useState<
    "guest" | "admin-host" | "usher" | null
  >(null);

  // Load stored credentials on component mount
  useEffect(() => {
    const loadStoredCredentials = async () => {
      try {
        const storedData = await secureStorage.getStoredCredentials();
        if (storedData) {
          setDefaultEmail(storedData.email);
        }
      } catch (error) {
        logError("Failed to load stored credentials", error);
      }
    };

    loadStoredCredentials();
  }, []);

  const verifyInvitationToken = async (token: string) => {
    setVerifyingToken(true);
    try {
      const response = await apiClient.get(
        `/ushers/invitations/accept/${token}/`
      );
      if (response.data.success) {
        setInvitationData(response.data.data);
        if (response.data.data.usher?.phone) {
          // Parse E.164 phone to get local number and country
          const { localNumber, countryCode: detectedCountry } = parseE164Phone(response.data.data.usher.phone);
          setPhoneNumber(localNumber);
          const country = findCountryByCode(detectedCountry);
          if (country) {
            setSelectedCountryCode(country.code);
          }
        }
        dispatch(
          addNotification({
            type: "success",
            title: "Invitation Valid",
            message: "You have been invited to usher for this event",
          })
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message || "Invalid invitation link";
      setLocalError(errorMessage);
      dispatch(
        addNotification({
          type: "error",
          title: "Invalid Invitation",
          message: errorMessage,
        })
      );
    } finally {
      setVerifyingToken(false);
    }
  };

  // Handle invitation token verification (for usher)
  useEffect(() => {
    const token = searchParams.get("token");
    if (token) {
      setInvitationToken(token);
      verifyInvitationToken(token);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Handle guest invitation token and redirect after login
  useEffect(() => {
    if (isAuthenticated && user) {
      const invitationToken = searchParams.get("invitation_token");
      const redirectPath = searchParams.get("redirect");
      
      if (invitationToken) {
        // Verify phone matches invitation
        const verifyInvitationPhone = async () => {
          try {
            const verifyResponse = await apiClient.get(
              `/invitation/${invitationToken}/verify-phone/`
            );
            
            if (verifyResponse.data.success && verifyResponse.data.phone_matches) {
              // Phone matches - redirect to invitation page
              navigate(redirectPath || `/invitation/${invitationToken}`);
            } else {
              // Phone doesn't match - normal login flow
              const role = user.active_role || user.role || 'guest';
              navigate(`/${role}/dashboard`);
            }
          } catch (error: any) {
            // Verification failed - normal login flow
            const role = user.active_role || user.role || 'guest';
            navigate(`/${role}/dashboard`);
          }
        };
        
        verifyInvitationPhone();
      } else {
        // Normal login - redirect to dashboard
        const role = user.active_role || user.role || 'guest';
        navigate(`/${role}/dashboard`);
      }
    }
  }, [isAuthenticated, user, searchParams, navigate]);

  const handleEmailSubmit = async (data: {
    email: string;
    password: string;
    rememberMe?: boolean;
  }) => {
    dispatch(clearError());

    if (data.rememberMe) {
      try {
        await secureStorage.storeCredentials(data.email, true);
      } catch (error) {
        logError("Failed to store credentials", error);
      }
    } else {
      secureStorage.clearCredentials();
    }

    dispatch(
      loginWithEmail({
        email: data.email,
        password: data.password,
        rememberMe: data.rememberMe || false,
      })
    );
  };

  const handlePhoneSubmit = async (rememberMeValue?: boolean) => {
    dispatch(clearError());
    setLocalError("");

    // Validate phone before submission
    if (!phoneValidation || !phoneValidation.valid) {
      setLocalError("Please enter a valid phone number");
      return;
    }

    // Use normalized phone (E.164 format) for submission
    const normalizedPhone = phoneValidation.normalized;
    const countryCode = phoneValidation.country;

    try {
      // If invitation token exists, use usher OTP endpoint directly
      if (invitationToken) {
        const requestData: any = {
          phone: normalizedPhone,
          country_code: countryCode,
          invitation_token: invitationToken,
        };

        const response = await apiClient.post(
          "/auth/usher/otp/send/",
          requestData
        );

        if (response.data.success) {
          setPhoneNumber(normalizedPhone);
          setSelectedCountryCode(countryCode);
          setRememberMe(rememberMeValue || false);
          setOtpEndpoint("usher");
          setStep("otp");
          dispatch(
            addNotification({
              type: "success",
              title: "OTP Sent",
              message: "Please check your phone for the verification code",
            })
          );
        }
        return;
      }

      // Check user roles first to determine appropriate OTP endpoint
      const roleCheckResponse = await apiClient.post("/auth/check-role/", {
        phone: normalizedPhone,
        country_code: countryCode,
      });

      if (!roleCheckResponse.data.success) {
        const errorMessage =
          roleCheckResponse.data.error?.message ||
          "Failed to check user. Please try again.";
        setLocalError(errorMessage);
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: errorMessage,
          })
        );
        return;
      }

      const { roles, user_exists, is_active } = roleCheckResponse.data.data;

      // Check if user exists
      if (!user_exists) {
        const errorMessage =
          "Phone number not registered. Please contact the event organizer.";
        setLocalError(errorMessage);
        dispatch(
          addNotification({
            type: "error",
            title: "Not Registered",
            message: errorMessage,
          })
        );
        return;
      }

      // Check if user is active
      if (!is_active) {
        const errorMessage =
          "Your account is inactive. Please contact the event organizer.";
        setLocalError(errorMessage);
        dispatch(
          addNotification({
            type: "error",
            title: "Account Inactive",
            message: errorMessage,
          })
        );
        return;
      }

      // Determine appropriate OTP endpoint based on roles (priority: admin/host > usher > guest)
      let otpRequestData: any = {
        phone: normalizedPhone,
        country_code: countryCode,
      };

      if (roles.includes("admin") || roles.includes("host")) {
        const result = await dispatch(
          requestAdminHostOTP({
            phone: normalizedPhone,
            country_code: countryCode,
          })
        );

        if (requestAdminHostOTP.fulfilled.match(result)) {
          setPhoneNumber(normalizedPhone);
          setSelectedCountryCode(countryCode);
          setOtpEndpoint("admin-host");
          setStep("otp");
          dispatch(
            addNotification({
              type: "success",
              title: "OTP Sent",
              message: "Please check your phone for the verification code",
            })
          );
        } else {
          const errorMessage =
            (result.payload as string) ||
            "Failed to send OTP. Please try again.";
          setLocalError(errorMessage);
          dispatch(
            addNotification({
              type: "error",
              title: "Error",
              message: errorMessage,
            })
          );
        }
      } else if (roles.includes("usher")) {
        const response = await apiClient.post(
          "/auth/usher/otp/send/",
          otpRequestData
        );

        if (response.data.success) {
          setPhoneNumber(normalizedPhone);
          setSelectedCountryCode(countryCode);
          setRememberMe(rememberMeValue || false);
          setOtpEndpoint("usher");
          setStep("otp");
          dispatch(
            addNotification({
              type: "success",
              title: "OTP Sent",
              message: "Please check your phone for the verification code",
            })
          );
        } else {
          const errorMessage =
            response.data.error?.message ||
            "Failed to send OTP. Please try again.";
          setLocalError(errorMessage);
          dispatch(
            addNotification({
              type: "error",
              title: "Error",
              message: errorMessage,
            })
          );
        }
      } else if (roles.includes("guest")) {
        const result = await dispatch(
          requestOTP({
            phone: normalizedPhone,
            country_code: countryCode,
          })
        );

        if (requestOTP.fulfilled.match(result)) {
          setPhoneNumber(normalizedPhone);
          setSelectedCountryCode(countryCode);
          setRememberMe(rememberMeValue || false);
          setOtpEndpoint("guest");
          setStep("otp");
          dispatch(
            addNotification({
              type: "success",
              title: "OTP Sent",
              message: "Please check your phone for the verification code",
            })
          );
        } else {
          const errorMessage =
            (result.payload as string) ||
            "Failed to send OTP. Please try again.";
          setLocalError(errorMessage);
          dispatch(
            addNotification({
              type: "error",
              title: "Error",
              message: errorMessage,
            })
          );
        }
      } else {
        // User exists but has no valid roles
        const errorMessage =
          "Your account does not have a valid role. Please contact the event organizer.";
        setLocalError(errorMessage);
        dispatch(
          addNotification({
            type: "error",
            title: "Invalid Role",
            message: errorMessage,
          })
        );
      }
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error?.message ||
        "Failed to send OTP. Please try again.";
      setLocalError(errorMessage);
      dispatch(
        addNotification({
          type: "error",
          title: "Error",
          message: errorMessage,
        })
      );
    }
  };

  const handleOTPSubmit = async (otp: string) => {
    dispatch(clearError());
    setLocalError("");

    if (!otp || otp.length !== 6) {
      return;
    }

    // If invitation token exists, use usher verification
    if (invitationToken) {
      const requestData: any = {
        phone: phoneNumber,
        otp_code: otp,
        country_code: selectedCountryCode,
        remember_me: rememberMe,
        invitation_token: invitationToken,
      };

      try {
        const result = await dispatch(verifyUsherOTP(requestData));

        if (verifyUsherOTP.fulfilled.match(result)) {
          dispatch(
            addNotification({
              type: "success",
              title: "Login Successful",
              message: "Welcome to the usher dashboard!",
            })
          );
        } else {
          const errorMessage =
            (result.payload as string) || "Invalid OTP. Please try again.";
          setLocalError(errorMessage);
        }
      } catch (error: any) {
        const errorMessage = "An unexpected error occurred. Please try again.";
        setLocalError(errorMessage);
      }
    } else {
      // Use the same endpoint that successfully sent the OTP
      if (otpEndpoint === "guest") {
        const result = await dispatch(
          verifyOTP({
            phone: phoneNumber,
            otp_code: otp,
            country_code: selectedCountryCode,
            remember_me: rememberMe,
          })
        );

        if (verifyOTP.rejected.match(result)) {
          const errorMessage =
            (result.payload as string) || "Invalid OTP. Please try again.";
          setLocalError(errorMessage);
        }
      } else if (otpEndpoint === "admin-host") {
        const result = await dispatch(
          verifyAdminHostOTP({
            phone: phoneNumber,
            otp_code: otp,
            country_code: selectedCountryCode,
            remember_me: rememberMe,
          })
        );

        if (verifyAdminHostOTP.rejected.match(result)) {
          const errorMessage =
            (result.payload as string) || "Invalid OTP. Please try again.";
          setLocalError(errorMessage);
        }
      } else if (otpEndpoint === "usher") {
        try {
          const requestData: any = {
            phone: phoneNumber,
            otp_code: otp,
            country_code: selectedCountryCode,
            remember_me: rememberMe,
          };

          const result = await dispatch(verifyUsherOTP(requestData));

          if (verifyUsherOTP.rejected.match(result)) {
            const errorMessage =
              (result.payload as string) || "Invalid OTP. Please try again.";
            setLocalError(errorMessage);
          }
        } catch (usherError: any) {
          const errorMessage =
            usherError.response?.data?.error?.message ||
            "Invalid OTP. Please try again.";
          setLocalError(errorMessage);
        }
      } else {
        // Fallback: try all endpoints in order if endpoint is not tracked
        const result = await dispatch(
          verifyOTP({
            phone: phoneNumber,
            otp_code: otp,
            country_code: selectedCountryCode,
            remember_me: rememberMe,
          })
        );

        if (verifyOTP.rejected.match(result)) {
          const hostResult = await dispatch(
            verifyAdminHostOTP({
              phone: phoneNumber,
              otp_code: otp,
              country_code: selectedCountryCode,
              remember_me: rememberMe,
            })
          );

          if (verifyAdminHostOTP.rejected.match(hostResult)) {
            try {
              const requestData: any = {
                phone: phoneNumber,
                otp_code: otp,
                country_code: selectedCountryCode,
                remember_me: rememberMe,
              };

              const usherResult = await dispatch(verifyUsherOTP(requestData));

              if (verifyUsherOTP.rejected.match(usherResult)) {
                const errorMessage =
                  (usherResult.payload as string) ||
                  "Invalid OTP. Please try again.";
                setLocalError(errorMessage);
              }
            } catch (usherError: any) {
              const errorMessage =
                usherError.response?.data?.error?.message ||
                "Invalid OTP. Please try again.";
              setLocalError(errorMessage);
            }
          }
        }
      }
    }
  };

  const handleResendOTP = async () => {
    // Use the same endpoint that was used to send the initial OTP
    if (invitationToken || otpEndpoint === "usher") {
      try {
        const requestData: any = {
          phone: phoneNumber,
          country_code: selectedCountryCode,
        };

        if (invitationToken) {
          requestData.invitation_token = invitationToken;
        }

        const response = await apiClient.post(
          "/auth/usher/otp/send/",
          requestData
        );

        if (response.data.success) {
          dispatch(
            addNotification({
              type: "info",
              title: "OTP Resent",
              message: "A new verification code has been sent to your phone",
            })
          );
        }
      } catch (error: any) {
        const errorMessage =
          error.response?.data?.error?.message ||
          "Failed to resend OTP. Please try again.";
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: errorMessage,
          })
        );
      }
    } else if (otpEndpoint === "guest") {
      const result = await dispatch(
        requestOTP({
          phone: phoneNumber,
          country_code: selectedCountryCode,
        })
      );

      if (requestOTP.fulfilled.match(result)) {
        dispatch(
          addNotification({
            type: "info",
            title: "OTP Resent",
            message: "A new verification code has been sent to your phone",
          })
        );
      } else {
        const errorMessage = "Failed to resend OTP. Please try again.";
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: errorMessage,
          })
        );
      }
    } else if (otpEndpoint === "admin-host") {
      const result = await dispatch(
        requestAdminHostOTP({
          phone: phoneNumber,
          country_code: selectedCountryCode,
        })
      );

      if (requestAdminHostOTP.fulfilled.match(result)) {
        dispatch(
          addNotification({
            type: "info",
            title: "OTP Resent",
            message: "A new verification code has been sent to your phone",
          })
        );
      } else {
        const errorMessage = "Failed to resend OTP. Please try again.";
        dispatch(
          addNotification({
            type: "error",
            title: "Error",
            message: errorMessage,
          })
        );
      }
    } else {
      // Fallback: try guest first, then host, then usher
      const guestResult = await dispatch(
        requestOTP({
          phone: phoneNumber,
          country_code: selectedCountryCode,
        })
      );

      if (requestOTP.rejected.match(guestResult)) {
        const hostResult = await dispatch(
          requestAdminHostOTP({
            phone: phoneNumber,
            country_code: selectedCountryCode,
          })
        );

        if (requestAdminHostOTP.rejected.match(hostResult)) {
          try {
            const requestData: any = {
              phone: phoneNumber,
              country_code: selectedCountryCode,
            };

            const response = await apiClient.post(
              "/auth/usher/otp/send/",
              requestData
            );

            if (response.data.success) {
              setOtpEndpoint("usher");
              dispatch(
                addNotification({
                  type: "info",
                  title: "OTP Resent",
                  message:
                    "A new verification code has been sent to your phone",
                })
              );
            }
          } catch (error: any) {
            const errorMessage =
              error.response?.data?.error?.message ||
              "Failed to resend OTP. Please try again.";
            dispatch(
              addNotification({
                type: "error",
                title: "Error",
                message: errorMessage,
              })
            );
          }
        } else {
          setOtpEndpoint("admin-host");
          dispatch(
            addNotification({
              type: "info",
              title: "OTP Resent",
              message: "A new verification code has been sent to your phone",
            })
          );
        }
      } else {
        setOtpEndpoint("guest");
        dispatch(
          addNotification({
            type: "info",
            title: "OTP Resent",
            message: "A new verification code has been sent to your phone",
          })
        );
      }
    }
  };

  const handleBackToPhone = () => {
    setStep("phone");
    setOtpEndpoint(null);
    dispatch(clearError());
  };

  const getSubtitle = () => {
    if (verifyingToken) {
      return "Verifying invitation...";
    }
    if (invitationData) {
      return `You've been invited to usher for: ${invitationData.event_title}`;
    }
    if (loginMethod === "email") {
      return "Sign in with your email and password";
    }
    return step === "phone"
      ? "Enter your WhatsApp phone number to receive a verification code"
      : `We sent a code to ${phoneNumber}`;
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-hidden relative">
      {/* Background Graphics */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-96 h-96 bg-gradient-to-br from-[#33FF00]/10 to-[#2BD400]/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-gradient-to-tr from-[#2BD400]/10 to-[#33FF00]/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
      </div>

      <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
        <div className="max-w-6xl w-full">
          {/* Back Button */}
          <div className="mb-6">
            <a
              href="/"
              className="inline-flex items-center text-gray-300 hover:text-white transition-colors"
            >
              <svg
                className="h-4 w-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back to Home
            </a>
          </div>

          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img
                src="/logo.jpg"
                alt="Mirsal Logo"
                className="h-12 w-12 rounded-lg object-cover"
              />
            </div>
            <h1 className="text-4xl font-bold text-white mb-2">Welcome </h1>
            <p className="text-xl text-gray-300">{getSubtitle()}</p>
          </div>

          {/* Token Verification Loading */}
          {verifyingToken && (
            <div className="text-center py-8">
              <div className="w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-300">Verifying invitation...</p>
            </div>
          )}

          {/* Main Card - Single Column Layout */}
          {!verifyingToken && (
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-3xl p-8 lg:p-12 max-w-md mx-auto">
              {/* Success Message for Valid Invitation */}
              {invitationData && (
                <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-6">
                  <div className="flex items-center">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                    <p className="text-sm text-green-400">
                      Invitation verified! Please log in with your phone number
                      to accept the usher role.
                    </p>
                  </div>
                </div>
              )}

              {/* Login Form */}
              <div className="flex flex-col justify-center">
                {loginMethod === "phone" ? (
                  step === "phone" ? (
                    <>
                      {/* Error Message */}
                      {(localError || error) && (
                        <AuthErrorDisplay
                          error={localError || error || undefined}
                          type="error"
                        />
                      )}

                      <form 
                        className="space-y-6" 
                        onSubmit={(e) => {
                          e.preventDefault();
                          handlePhoneSubmit(rememberMe);
                        }}
                      >
                        {/* Phone Input with Validation */}
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                            Phone Number
                          </label>
                          <PhoneInputWithValidation
                            value={phoneNumber}
                            onChange={(phoneValue, countryCodeValue) => {
                              setPhoneNumber(phoneValue);
                              setSelectedCountryCode(countryCodeValue);
                            }}
                            onValidationChange={setPhoneValidation}
                            error={localError || error || undefined}
                            required
                            showValidationResult={false}
                            placeholder="Enter phone number"
                          />
                        </div>

                        {/* Remember Me Checkbox */}
                        <div className="flex items-center">
                          <input
                            id="rememberMe"
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="h-4 w-4 text-[#33FF00] focus:ring-[#33FF00] border-gray-400 rounded bg-white/5"
                          />
                          <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-300">
                            Remember me for 30 days
                          </label>
                        </div>

                        {/* Submit Button */}
                        <Button
                          type="submit"
                          disabled={!phoneValidation?.valid || loading}
                          className="w-full py-3 bg-gradient-to-r from-[#33FF00] to-[#2BD400] hover:from-[#2BD400] hover:to-[#33FF00] disabled:from-gray-600 disabled:to-gray-700 text-white rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed disabled:transform-none disabled:text-white"
                          loading={loading}
                        >
                          {loading ? 'Sending...' : 'Send Verification Code'}
                        </Button>
                      </form>

                      {/* Host Login Link */}
                      <div className="mt-6 text-center">
                        <button
                          type="button"
                          onClick={() => setLoginMethod("email")}
                          className="inline-flex items-center text-sm text-gray-300 hover:text-gray-200 transition-colors font-medium"
                        >
                          <Mail className="w-4 h-4 mr-1" />
                          Login using email
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Error Message */}
                      {(localError || error) && (
                        <AuthErrorDisplay
                          error={localError || error || undefined}
                          type="error"
                        />
                      )}

                      <OTPVerificationForm
                        onSubmit={handleOTPSubmit}
                        onResend={handleResendOTP}
                        onBack={handleBackToPhone}
                        phoneNumber={phoneNumber}
                        loading={loading}
                        error={localError || error || undefined}
                        accentColor="green"
                      />
                    </>
                  )
                ) : (
                  <>
                    {/* Error Message */}
                    {(localError || error) && (
                      <AuthErrorDisplay
                        error={localError || error || undefined}
                        type="error"
                      />
                    )}

                    <EmailPasswordForm
                      onSubmit={handleEmailSubmit}
                      loading={loading}
                      error={localError || error || undefined}
                      defaultEmail={defaultEmail}
                      accentColor="green"
                    />

                    {/* Toggle back to Phone */}
                    <div className="mt-6 text-center">
                      <button
                        type="button"
                        onClick={() => setLoginMethod("phone")}
                        className="text-sm text-gray-300 hover:text-gray-200 transition-colors font-medium"
                      >
                        Sign in with phone number instead?
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="text-center mt-8 text-sm text-gray-400">
            <p>Need help? Contact support@mirsaljo.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedLoginPage;
